/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { useCallback } from 'preact/hooks';
import { toChildArray } from 'preact';
import { Context } from './TrainContext';

import { useComponentTheme } from '../hooks/UNSAFE_useComponentTheme';
import { useTestId, TestIdProps } from '../hooks/UNSAFE_useTestId';
import { TrainStyles } from './themes/TrainStyles.css';
import { TrainRedwoodTheme } from './themes/redwood/TrainTheme';

type TrainProps = {
  /**
   * Id of the currently selected step.
   */
  selectedStep: string;

  /**
   * Children of Train which are of type Step.
   */
  children: JSX.Element[];

  /**
   * A callback function to be invoked when the Step is selected.
   */
  onSelect?: (detail: selectDetail) => void;
} & TestIdProps;

export type selectDetail = {
  /**
   * Click event object
   */
  event: Event;
  /**
   * To step id
   */
  toStep: string;
};

/**
The JET Train component serves as a visual navigator enabling users to traverse through different 'steps'. Each 'step' is represented by the Step sub-component which can manifest its own state - 'visited', 'unvisited', or 'disabled', and is capable of communicating various message types including 'error', 'confirmation', 'warning', or 'info'.
 */
export function Train({ onSelect, selectedStep, children, testId }: TrainProps) {
  const { classes, styles } = useComponentTheme<never, TrainStyles>(TrainRedwoodTheme);

  const testIdProps = useTestId(testId);

  const selectedStepIndex = children.findIndex((step) => step.props.id === selectedStep);

  const onStepSelect = useCallback(
    (event: Event, stepId: string) => {
      onSelect?.({
        event: event,
        toStep: stepId
      });
    },
    [onSelect]
  );

  const trainContext = {
    onSelect: onStepSelect,
    selectedStep: selectedStep,
    selectedStepIndex: selectedStepIndex,
    numberOfSteps: children.length,
    index: 0
  };

  const childrenArray = toChildArray(children).filter(Boolean);

  return (
    <div class={classes} {...testIdProps}>
      {childrenArray.map((step, index) => (
        <Context.Provider value={{ ...trainContext, index }}>
          <div class={styles.baseListStyle}>{step}</div>
        </Context.Provider>
      ))}
    </div>
  );
}
